import styled from '@emotion/styled'

import { fonts, colors } from 'component-library/__styling/settings'
import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { metrics } from 'component-library/__styling/settings'
import { easing } from 'component-library/__styling/settings/easing'

import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { zIndex } from 'component-library/__styling/settings/z-index'
import { suggestionsClassName as searchSuggestionsClassName } from 'component-library/search/SearchForm/SearchForm.styled'

const navScrollShadowSize = 30
const navPaddingSize = metrics.thewest.margins.outerMargin

export const StyledNav = styled('nav')({
    maxWidth: '100%',
    fontWeight: 600,
    borderWidth: calcRem(1, 0),
    borderColor: colors.thewest.greySeal,
    display: 'flex',
    justifyContent: 'center',
    fontFamily: fonts.thewest.sansSerif,
    borderStyle: 'solid',
    background: colors.white,

    [`& .${searchSuggestionsClassName}`]: {
        left: navPaddingSize + 1, // +1 is to include the nav border
        width: metrics.thewest.mainContentWidth * 0.45,
        maxWidth: `calc(100% - ${metrics.thewest.margins.outerMargin * 2}px)`,
        maxHeight: '30vh',
    },
})

export const StyledNavWrapper = styled('div')({
    minWidth: 0, // Prevent defaulting to auto. In FF auto means scale to contents making the nav too wide on mobile.
    position: 'relative',
    display: 'flex',
    // safari has issue if we don't manually force the width of this container to grow, it will
    // not shrink the nav responsively. The hack here is to give it grow rule with an initial
    // width of 1
    flexGrow: 1,
    width: 1,
    flexBasis: '100%',
    maxWidth: metrics.thewest.mainContentWidth,
})

export const StyledMainNav = styled('nav')({
    position: 'relative',
    display: 'flex',
    flexGrow: 1,
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    whiteSpace: 'nowrap',
    WebkitOverflowScrolling: 'touch',
    MsOverflowStyle: 'none',
    padding: `0 0  0 ${calcRem(navPaddingSize)}`,

    // This is the small shadow that gives the nav the effect that there is
    // more for the user to scroll towards. Each radial gradient is rendered like
    // a shadow and applied to the edges of the div, producing the layer effect.
    // Note on the first gradient could use calc for positioning but IE11 that doesn't
    // like that so much.
    backgroundImage: `
        radial-gradient(
            ellipse ${navScrollShadowSize}px 210% at 101% center,
                ${colors.thewest.greyKoala} 0%,
                transparent 50%),
        radial-gradient(
            ellipse ${navScrollShadowSize}px 210% at ${navScrollShadowSize * -0.25}px center,
                ${colors.thewest.greyKoala} 0%,
                transparent 50%)
    `,

    [breakpoint('lg')]: {
        display: 'flex',
        textAlign: 'left',
    },

    '&::-webkit-scrollbar': {
        display: 'none',
    },

    // pseudo elements are used to mask the above StyledNavScroll gradient so
    // we get a cool fade effect. We could use background-attachment: local
    // for this but that has issues with -webkit-overflow-scrolling.

    // Using flexGrow + pseudo element on the last child here is by design - we
    // use the last child in nav list to position the pseudo element for mask shadow

    '&::after, &::before': {
        content: `''`,
        minWidth: navScrollShadowSize,
        zIndex: zIndex.thewest.mainNavContent,
    },

    '&::after': {
        position: 'relative',
        flexGrow: 1,
        // must use rgba instead of "transparent" keyword. Some browsers base transparent at black
        backgroundImage: `linear-gradient(to right, rgba(255, 255, 255, 0), white 40%)`,
    },

    '&::before': {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: zIndex.thewest.mainNavScrollShadow,
        // must use rgba instead of "transparent" keyword. Some browsers base transparent at black
        backgroundImage: `linear-gradient(to left, rgba(255, 255, 255, 0), white 40%)`,
    },
})

export const StyledSupportingNav = styled('div')<{ includeSubNav: boolean | undefined }>(
    {
        padding: `0 ${calcRem(navPaddingSize)}`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    }, // if the subnav is not included, and the subscribe link has been hidden due to the breakpoints,
    // we should avoid showing an empty square cased by the padding
    ({ includeSubNav }) =>
        conditionalStyles(includeSubNav !== true, {
            padding: 0,
            [breakpoint('lg')]: {
                padding: `0 ${calcRem(navPaddingSize)}`,
            },
        })
)

export const StyledSubscribeLink = styled('button')({
    display: 'none',
    fontFamily: fonts.thewest.sansSerif,
    backgroundColor: colors.thewest.greyDolphin,
    border: 'none',
    color: colors.thewest.greyGorilla,
    '&:hover': {
        backgroundColor: colors.thewest.greyGorilla,
        color: colors.white,
    },

    [breakpoint('lg')]: {
        fontSize: calcRem(14),
        fontWeight: 700,
        display: 'inline-block',
        flex: '0 1 auto',
        padding: calcRem(9, 13),
        marginRight: calcRem(10),
        lineHeight: 1,
        borderRadius: 4,
        textDecoration: 'none',
        textTransform: 'uppercase',
        transition: `color ${easing.easeOut.fast}, background ${easing.easeOut.fast}`,
    },

    '&:focus, &.is-active': {
        textDecoration: 'underline',
        outline: 'none',
    },
})

export const StyledSubnavTrigger = styled('button')(({ theme }) => ({
    whiteSpace: 'nowrap',
    fontSize: calcRem(14),
    fontWeight: 700,
    padding: calcRem(0, 10),
    height: calcRem(32),
    lineHeight: 1,
    borderRadius: 4,
    border: 0,
    color: colors.white,
    textDecoration: 'none',
    textTransform: 'uppercase',
    transition: `color ${easing.easeOut.fast}, background ${easing.easeOut.fast}, boxShadow ${easing.easeOut.fast}`,
    fontFamily: fonts.thewest.sansSerif,
    backgroundColor: theme.sectionValues.primaryColor,

    '&:hover': {
        background: colors.thewest.greyGorilla,
    },

    '&:focus, &.is-active': {
        textDecoration: 'underline',
        outline: 'none',
        background: colors.thewest.greyKoala,
    },
    cursor: 'pointer',

    [breakpoint('md')]: {
        padding: calcRem(0, 13),
    },
}))

export interface StyledSubnavTriggerIconProps {
    subNavOpen: boolean
}

export const StyledSubnavTriggerIcon = styled('div')<StyledSubnavTriggerIconProps>(
    {
        position: 'relative',
        display: 'inline-block',
        verticalAlign: 'middle',
        width: calcRem(18),
        height: calcRem(2),
        background: colors.white,
        transition: `background ${easing.easeOut.fast}`,
        transform: `translateY(-1px)`,

        [breakpoint('md')]: {
            marginRight: 8,
        },

        '&::before, &::after': {
            position: 'absolute',
            transformOrigin: 'center',
            right: 0,
            left: 0,
            height: calcRem(2),
            background: colors.white,
            content: `''`,
            transition: `transform ${easing.easeOut.med}, top ${easing.easeOut.fast}, bottom ${easing.easeOut.fast}`,
        },

        '&::before': {
            top: calcRem(5),
        },

        '&::after': {
            bottom: calcRem(5),
        },
    },
    props =>
        conditionalStyles(props.subNavOpen === true, {
            background: 'transparent',
            '&::before': {
                transform: 'rotate(45deg)',
                top: 0,
            },
            '&::after': {
                transform: 'rotate(-45deg)',
                bottom: 0,
            },
        })
)

export const StyledSubNavTriggerText = styled('span')({
    display: 'none',

    [breakpoint('md')]: {
        display: 'inline-block',
    },
})
